import { createContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useApprovals, useApprove, useReject, useCurrentUser } from '@/hooks/useApi'
import { pushSupported, getExistingSubscription, subscribeToPush, unsubscribeFromPush } from '@/lib/push'

export interface ApprovalItem {
  token: string
  tool: string
  tier: number
  summary: string
  created_at: string
  expires_in: number
  type?: string
  actionable?: boolean
  source_name?: string
  freshness?: number
  requested_by?: string | null
  requested_by_role?: string | null
  approved_by?: string | null
  approved_by_role?: string | null
  approved_at?: string | null
  executed_at?: string | null
  error?: string | null
  status?: string | null
}

export interface ApprovalDetail {
  success: boolean
  token: string
  tool: string
  tier: number
  params: Record<string, unknown>
  created_at: string
  requested_by?: string | null
  requested_by_role?: string | null
  approved_by?: string | null
  approved_by_role?: string | null
  approved_at?: string | null
  executed_at?: string | null
  error?: string | null
  status?: string | null
}

interface ApprovalCentreValue {
  principalId: string
  approvals: ApprovalItem[]
  pendingCount: number
  isLoading: boolean
  isFetching: boolean
  pollError: string | null
  now: number
  actionToken: string | null
  pushEnabled: boolean | null
  pushBusy: boolean
  refresh: () => Promise<void>
  approve: (token: string) => Promise<unknown>
  deny: (token: string) => Promise<unknown>
  loadReview: (token: string) => Promise<ApprovalDetail>
  togglePush: () => Promise<void>
}

export const ApprovalCentreContext = createContext<ApprovalCentreValue | null>(null)

function getExpiresAt(createdAt: string, expiresIn: number): number {
  return new Date(createdAt).getTime() + expiresIn * 1000
}

export function ApprovalCentreProvider({ children }: { children: ReactNode }) {
  const currentUser = useCurrentUser()
  const queryClient = useQueryClient()
  const previousPrincipal = useRef<string>('')
  const query = useApprovals(true)
  const approveMutation = useApprove()
  const rejectMutation = useReject()
  const [now, setNow] = useState(Date.now())
  const [actionToken, setActionToken] = useState<string | null>(null)
  const [pushEnabled, setPushEnabled] = useState<boolean | null>(null)
  const [pushBusy, setPushBusy] = useState(false)

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const nextPrincipal = currentUser.user_id
    const previous = previousPrincipal.current
    if (previous && previous !== nextPrincipal) {
      queryClient.removeQueries({ queryKey: ['dashboard', 'approvals', previous], exact: true })
      queryClient.removeQueries({ queryKey: ['dashboard', 'approval-results', previous], exact: true })
      setActionToken(null)
    }
    previousPrincipal.current = nextPrincipal
  }, [currentUser.user_id, queryClient])

  useEffect(() => {
    if (!pushSupported()) {
      setPushEnabled(false)
      return
    }
    setPushEnabled(null)
    getExistingSubscription().then(sub => {
      setPushEnabled(!!sub)
      if (sub && currentUser.user_id) {
        api.pushSubscribe(sub.toJSON()).catch(() => {})
      }
    }).catch(() => setPushEnabled(false))
  }, [currentUser.user_id])

  const approvals = useMemo(() => {
    const items = ((query.data?.approvals || []) as ApprovalItem[])
    return items.filter(item => getExpiresAt(item.created_at, item.expires_in) > now - 3000)
  }, [query.data, now])

  const pendingCount = useMemo(
    () => approvals.filter(item => getExpiresAt(item.created_at, item.expires_in) > now).length,
    [approvals, now],
  )

  const refresh = useCallback(async () => {
    await query.refetch()
  }, [query])

  const approve = useCallback(async (token: string) => {
    setActionToken(token)
    try {
      return await approveMutation.mutateAsync({ requestId: token })
    } finally {
      setActionToken(null)
    }
  }, [approveMutation])

  const deny = useCallback(async (token: string) => {
    setActionToken(token + ':deny')
    try {
      return await rejectMutation.mutateAsync({ requestId: token })
    } finally {
      setActionToken(null)
    }
  }, [rejectMutation])

  const loadReview = useCallback(async (token: string) => {
    return await api.approvalDetail(token) as ApprovalDetail
  }, [])

  const togglePush = useCallback(async () => {
    setPushBusy(true)
    try {
      if (pushEnabled) {
        await unsubscribeFromPush()
        setPushEnabled(false)
      } else {
        const result = await subscribeToPush()
        if (!result.success) throw new Error(result.error || 'Could not enable approval notifications')
        setPushEnabled(true)
      }
    } finally {
      setPushBusy(false)
    }
  }, [pushEnabled])

  const value = useMemo<ApprovalCentreValue>(() => ({
    principalId: currentUser.user_id,
    approvals,
    pendingCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    pollError: query.error instanceof Error ? query.error.message : null,
    now,
    actionToken,
    pushEnabled,
    pushBusy,
    refresh,
    approve,
    deny,
    loadReview,
    togglePush,
  }), [
    currentUser.user_id, approvals, pendingCount, query.isLoading, query.isFetching, query.error,
    now, actionToken, pushEnabled, pushBusy, refresh, approve, deny, loadReview, togglePush,
  ])

  return <ApprovalCentreContext.Provider value={value}>{children}</ApprovalCentreContext.Provider>
}
